// dashboard/components/Overview.jsx
import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  LinearProgress,
  Chip,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  IconButton,
  Grow,
  Zoom,
  Menu,
  MenuItem,
  Dialog,
  DialogContent,
  CircularProgress,
  Tooltip,
  Alert,
  Snackbar,
} from "@mui/material";
import {
  AccountBalanceWallet,
  School,
  Telegram,
  TrendingUp,
  Receipt,
  ArrowForward,
  MoreVert,
  ExpandMore,
  CameraAlt,
  Close,
} from "@mui/icons-material";
import { useAuth } from "../../context/authContext";
import api from "../../api/axios";
import { styled } from "@mui/material/styles";
import YourCoursesCards from "./YourCoursesCards";

// Styled Interactive Stat Card
const InteractiveStatCard = styled(Card)(({ color }) => ({
  borderRadius: 16,
  cursor: "pointer",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  border: "1px solid #eef2f7",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    background: color || "#4a6cf7",
    opacity: 0,
    transition: "opacity 0.3s ease",
  },
  "&:hover": {
    transform: "translateY(-6px)",
    boxShadow: `0 12px 40px ${color ? `${color}30` : "rgba(74, 108, 247, 0.15)"}`,
    "&::before": { opacity: 1 },
  },
  "&:active": {
    transform: "scale(0.97)",
    transition: "transform 0.1s ease",
  },
}));

// Styled Interactive Activity Item
const InteractiveActivityItem = styled(ListItem)({
  borderRadius: 8,
  cursor: "pointer",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: "rgba(74, 108, 247, 0.05)",
    transform: "translateX(4px)",
  },
  "&:active": {
    transform: "scale(0.98)",
    transition: "transform 0.1s ease",
  },
});

const Overview = () => {
  const { user, updateUser } = useAuth();
  const [hoveredStat, setHoveredStat] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // ============================================================
  // PROFILE PHOTO STATE
  // ============================================================
  const fileInputRef = useRef(null);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [photoViewerOpen, setPhotoViewerOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // Stats data
  const stats = [
    {
      label: "Total Courses",
      value: "19",
      change: "+2 New",
      changeType: "positive",
      icon: <School />,
      color: "#4a6cf7",
      bgColor: "rgba(74, 108, 247, 0.08)",
    },
    {
      label: "Hours Spent",
      value: "21h 30m",
      change: "↓ 15%",
      changeType: "negative",
      icon: <TrendingUp />,
      color: "#f5a623",
      bgColor: "rgba(245, 166, 35, 0.08)",
    },
    {
      label: "Active Subscriptions",
      value: "3",
      change: "Active",
      changeType: "positive",
      icon: <Telegram />,
      color: "#2ecc71",
      bgColor: "rgba(46, 204, 113, 0.08)",
    },
    {
      label: "Wallet Balance",
      value: "$12,450",
      change: "+$500",
      changeType: "positive",
      icon: <AccountBalanceWallet />,
      color: "#e74c3c",
      bgColor: "rgba(231, 76, 60, 0.08)",
    },
  ];

  // Forex Course data for YourCoursesCards
  const forexCourses = [
    { name: "EUR/USD Mastery", status: "Active", daysLeft: 45, progress: 75 },
    { name: "GBP/USD Strategies", status: "Active", daysLeft: 30, progress: 60 },
    { name: "USD/JPY Trading", status: "Finished", daysLeft: 0, progress: 100 },
    { name: "AUD/USD Fundamentals", status: "Paused", daysLeft: 21, progress: 40 },
  ];

  const activities = [
    { title: "EUR/USD Mastery", user: "John Doe", date: "19 April", type: "course" },
    { title: "Live Trading Session", user: "Micak Doe", date: "12 June", type: "live" },
    { title: "Forex Webinar", user: "Potar Doe", date: "27 July", type: "art" },
  ];

  // ============================================================
  // GET USER INITIALS
  // ============================================================
  const getUserInitials = () => {
    if (!user) return "U";
    if (user.first_name && user.last_name) {
      return `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`.toUpperCase();
    }
    return user.first_name ? user.first_name.charAt(0).toUpperCase() : "U";
  };

  // ============================================================
  // GET PROFILE PHOTO
  // ============================================================
  const getProfilePhoto = () => {
    // First check preview (for newly uploaded images)
    if (photoPreview) return photoPreview;
    
    // Then check the user object from auth context
    if (user?.profilePhoto) return user.profilePhoto;
    if (user?.profile_photo) return user.profile_photo;
    
    // Finally check local state
    if (profilePhoto) return profilePhoto;
    
    return null;
  };

  // ============================================================
  // SHOW SNACKBAR
  // ============================================================
  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // ============================================================
  // HANDLE FILE SELECTION
  // ============================================================
  const handleChangePhotoClick = (event) => {
    event.stopPropagation();
    if (isUploadingPhoto) return;
    fileInputRef.current?.click();
  };

  // ============================================================
  // CONVERT FILE TO BASE64
  // ============================================================
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  // ============================================================
  // HANDLE PROFILE PHOTO CHANGE
  // ============================================================
  const handleProfilePhotoChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      showSnackbar("Please select a valid image file.", "error");
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      showSnackbar("Image is too large. Please select an image below 5MB.", "error");
      return;
    }

    try {
      setIsUploadingPhoto(true);

      // Create local preview
      const localPreview = URL.createObjectURL(file);
      setPhotoPreview(localPreview);

      // Convert image to base64
      const base64Image = await fileToBase64(file);

      // Log the upload
      console.log("Uploading profile photo...");

      // Send to backend
      const response = await api.put(
        "/users/profile/photo",
        { profilePhoto: base64Image }
      );

      console.log("Profile photo response:", response);

      // Check if response was successful
      if (response.status === 200 || response.status === 201) {
        const responseData = response.data;
        
        // Get the updated photo URL from response
        const updatedPhoto = responseData?.data?.profilePhoto || 
                           responseData?.profilePhoto || 
                           responseData?.user?.profilePhoto ||
                           responseData?.user?.profile_photo ||
                           base64Image;

        // Update local state
        setProfilePhoto(updatedPhoto);
        setPhotoPreview(null);

        // Update the user in auth context if available
        if (updateUser && typeof updateUser === 'function') {
          updateUser({ profilePhoto: updatedPhoto });
        }

        showSnackbar("Profile photo updated successfully!", "success");
        console.log("✅ Profile photo updated successfully");

      } else {
        throw new Error(response?.data?.message || "Failed to update profile photo");
      }

    } catch (error) {
      console.error("❌ Profile photo upload error:", error);
      
      // Remove temporary preview if upload failed
      setPhotoPreview(null);
      
      showSnackbar(
        error?.response?.data?.message || 
        error?.message || 
        "Failed to update profile picture. Please try again.",
        "error"
      );

    } finally {
      setIsUploadingPhoto(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // ============================================================
  // OPEN LARGE PROFILE PHOTO
  // ============================================================
  const handleViewProfilePhoto = () => {
    const photo = getProfilePhoto();
    if (!photo) return;
    setPhotoViewerOpen(true);
  };

  // ============================================================
  // HANDLERS
  // ============================================================
  const handleStatClick = (stat) => {
    console.log(`Clicked on ${stat.label}: ${stat.value}`);
  };

  const handleCourseClick = (course) => {
    console.log(`Clicked on course: ${course.name}`);
  };

  const handleActivityClick = (activity) => {
    console.log(`Clicked on activity: ${activity.title}`);
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box>
      {/* Welcome Section */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 3,
          border: "1px solid rgba(255,255,255,0.05)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          background: "linear-gradient(135deg, #001f3f 0%, #002a5c 100%)",
        }}
      >
        {/* Welcome Text */}
        <Box sx={{ textAlign: "left" }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: "#ffffff",
              mb: 0.5,
            }}
          >
            Hello {user?.first_name || "Trader"}, Welcome Back!
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "rgba(255,255,255,0.8)",
            }}
          >
            Your Forex journey continues! You've completed 3 courses this month.
          </Typography>
        </Box>

        {/* Profile Photo Area */}
        <Zoom in timeout={500}>
          <Box
            sx={{
              position: "relative",
              width: 92,
              height: 92,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {/* Hidden File Input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={handleProfilePhotoChange}
            />

            {/* Profile Avatar */}
            <Tooltip title="Click to view profile picture">
              <Box
                onClick={handleViewProfilePhoto}
                sx={{
                  position: "relative",
                  width: 80,
                  height: 80,
                  cursor: getProfilePhoto() ? "pointer" : "default",
                  borderRadius: "50%",
                  padding: "4px",
                  background: "linear-gradient(135deg, #2ecc71, #00a8ff)",
                  boxShadow: "0 6px 25px rgba(46, 204, 113, 0.35)",
                  transition: "transform 0.5s ease, box-shadow 0.5s ease",
                  "&:hover": {
                    transform: "rotate(360deg)",
                    boxShadow: "0 8px 35px rgba(46, 204, 113, 0.55)",
                  },
                  "&:hover .profile-avatar": {
                    transform: "scale(0.96)",
                  },
                  "&:hover .photo-overlay": {
                    opacity: 1,
                  },
                }}
              >
                <Avatar
                  className="profile-avatar"
                  src={getProfilePhoto() || undefined}
                  alt={user?.first_name ? `${user.first_name}'s profile` : "Profile picture"}
                  sx={{
                    width: "100%",
                    height: "100%",
                    bgcolor: "#2ecc71",
                    fontSize: 28,
                    fontWeight: 700,
                    color: "#ffffff",
                    border: "3px solid #001f3f",
                    transition: "transform 0.5s ease",
                    "& img": {
                      objectFit: "cover",
                    },
                  }}
                >
                  {!getProfilePhoto() && getUserInitials()}
                </Avatar>

                {/* Hover Overlay */}
                <Box
                  className="photo-overlay"
                  sx={{
                    position: "absolute",
                    inset: 4,
                    borderRadius: "50%",
                    background: "rgba(0,0,0,0.55)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    opacity: 0,
                    transition: "opacity 0.25s ease",
                    pointerEvents: "none",
                  }}
                >
                  <CameraAlt
                    sx={{
                      color: "#ffffff",
                      fontSize: 27,
                    }}
                  />
                </Box>
              </Box>
            </Tooltip>

            {/* Change Photo Button */}
            <Tooltip title="Change profile picture">
              <IconButton
                onClick={handleChangePhotoClick}
                disabled={isUploadingPhoto}
                sx={{
                  position: "absolute",
                  right: -4,
                  bottom: -2,
                  width: 34,
                  height: 34,
                  backgroundColor: "#2ecc71",
                  color: "#ffffff",
                  border: "3px solid #001f3f",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
                  "&:hover": {
                    backgroundColor: "#27ae60",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#777",
                    color: "#fff",
                  },
                }}
              >
                {isUploadingPhoto ? (
                  <CircularProgress
                    size={17}
                    thickness={5}
                    sx={{ color: "#ffffff" }}
                  />
                ) : (
                  <CameraAlt sx={{ fontSize: 17 }} />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        </Zoom>
      </Paper>

      {/* Profile Photo Viewer Dialog */}
      <Dialog
        open={photoViewerOpen}
        onClose={() => setPhotoViewerOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            backgroundColor: "#001f3f",
          },
        }}
      >
        <DialogContent
          sx={{
            p: 1,
            position: "relative",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 360,
          }}
        >
          <IconButton
            onClick={() => setPhotoViewerOpen(false)}
            aria-label="Close profile photo"
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
              zIndex: 2,
              color: "#fff",
              backgroundColor: "rgba(0,0,0,0.45)",
              "&:hover": { backgroundColor: "rgba(0,0,0,0.65)" },
            }}
          >
            <Close />
          </IconButton>

          {getProfilePhoto() ? (
            <Box
              component="img"
              src={getProfilePhoto()}
              alt="Profile picture"
              sx={{
                display: "block",
                width: "100%",
                maxHeight: "75vh",
                objectFit: "contain",
                borderRadius: 2,
              }}
            />
          ) : (
            <Avatar
              sx={{
                width: 180,
                height: 180,
                bgcolor: "#2ecc71",
                color: "#fff",
                fontSize: 64,
                fontWeight: 700,
              }}
            >
              {getUserInitials()}
            </Avatar>
          )}
        </DialogContent>
      </Dialog>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={stat.label}>
            <Grow in timeout={300 + index * 100}>
              <div>
                <InteractiveStatCard
                  color={stat.color}
                  onClick={() => handleStatClick(stat)}
                  onMouseEnter={() => setHoveredStat(index)}
                  onMouseLeave={() => setHoveredStat(null)}
                >
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#8a8a9a",
                            fontWeight: 500,
                            textTransform: "uppercase",
                            letterSpacing: ".5px",
                            fontSize: ".65rem",
                          }}
                        >
                          {stat.label}
                        </Typography>
                        <Typography
                          variant="h5"
                          sx={{ fontWeight: 700, color: "#1a1a2e", mt: 0.5 }}
                        >
                          {stat.value}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: stat.changeType === "positive" ? "#2ecc71" : "#e74c3c",
                            fontWeight: 600,
                          }}
                        >
                          {stat.changeType === "positive" ? "↑" : "↓"} {stat.change}
                        </Typography>
                      </Box>
                      <Avatar
                        sx={{
                          bgcolor: stat.bgColor,
                          color: stat.color,
                          width: 44,
                          height: 44,
                          transform: hoveredStat === index ? "scale(1.1) rotate(5deg)" : "scale(1)",
                          transition: "all .3s ease",
                        }}
                      >
                        {stat.icon}
                      </Avatar>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={[75, 60, 90, 45][index]}
                      sx={{
                        mt: 2,
                        height: 3,
                        borderRadius: 2,
                        bgcolor: "#eef2f7",
                        "& .MuiLinearProgress-bar": { bgcolor: stat.color },
                      }}
                    />
                  </CardContent>
                </InteractiveStatCard>
              </div>
            </Grow>
          </Grid>
        ))}
      </Grid>

      {/* Main Content Grid */}
      <Grid container spacing={4}>
        {/* Left Column - Course Progress */}
        <Grid item xs={12} md={7}>
          <Card
            sx={{
              borderRadius: 3,
              boxShadow: "0 1px 3px rgba(0,0,0,.06)",
              border: "1px solid #eef2f7",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              {/* Header with dropdown */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 3,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: "#1a1a2e" }}>
                    Your Courses
                  </Typography>
                  <Button
                    size="small"
                    endIcon={<ExpandMore />}
                    onClick={handleMenuClick}
                    sx={{
                      color: "#8a8a9a",
                      fontWeight: 500,
                      fontSize: "0.8rem",
                      textTransform: "none",
                      "&:hover": {
                        backgroundColor: "rgba(0,0,0,0.04)",
                      },
                    }}
                  >
                    Most Popular
                  </Button>
                </Box>
                <Button
                  size="small"
                  endIcon={<ArrowForward />}
                  sx={{
                    color: "#4a6cf7",
                    fontWeight: 500,
                  }}
                >
                  View All
                </Button>
              </Box>

              {/* Dropdown Menu */}
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleMenuClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
              >
                <MenuItem onClick={handleMenuClose}>Most Popular</MenuItem>
                <MenuItem onClick={handleMenuClose}>Recently Added</MenuItem>
                <MenuItem onClick={handleMenuClose}>In Progress</MenuItem>
                <MenuItem onClick={handleMenuClose}>Completed</MenuItem>
              </Menu>

              {/* Course Cards Grid using YourCoursesCards */}
              <Grid container spacing={2}>
                {forexCourses.map((course) => (
                  <Grid item xs={12} sm={6} key={course.name}>
                    <YourCoursesCards
                      course={course}
                      onClick={() => handleCourseClick(course)}
                    />
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Activities */}
        <Grid item xs={12} md={5}>
          {/* Recent Activities */}
          <Card
            sx={{
              borderRadius: 3,
              boxShadow: "0 1px 3px rgba(0,0,0,.06)",
              border: "1px solid #eef2f7",
              mb: 3,
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, color: "#1a1a2e" }}>
                  Recent Activities
                </Typography>
                <IconButton size="small" sx={{ color: "#8a8a9a" }}>
                  <MoreVert fontSize="small" />
                </IconButton>
              </Box>
              <List sx={{ p: 0 }}>
                {activities.map((activity, index) => (
                  <React.Fragment key={activity.title}>
                    <InteractiveActivityItem
                      onClick={() => handleActivityClick(activity)}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: "rgba(74, 108, 247, .08)",
                            color: "#4a6cf7",
                          }}
                        >
                          {activity.type === "course" ? (
                            <School fontSize="small" />
                          ) : activity.type === "live" ? (
                            <TrendingUp fontSize="small" />
                          ) : (
                            <Receipt fontSize="small" />
                          )}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ fontWeight: 500, color: "#1a1a2e" }}>
                            {activity.title}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" sx={{ color: "#8a8a9a" }}>
                            {activity.user} • {activity.date}
                          </Typography>
                        }
                      />
                    </InteractiveActivityItem>
                    {index < activities.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <Card
            sx={{
              borderRadius: 3,
              boxShadow: "0 1px 3px rgba(0,0,0,.06)",
              border: "1px solid #eef2f7",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={2}>
                {[
                  {
                    label: "Total Courses",
                    value: "19",
                    change: "↑ 2 New",
                    color: "#4a6cf7",
                    changeColor: "#2ecc71",
                  },
                  {
                    label: "Hours Spent",
                    value: "21h 30m",
                    change: "↓ 15%",
                    color: "#f5a623",
                    changeColor: "#e74c3c",
                  },
                ].map((stat) => (
                  <Grid item xs={6} key={stat.label}>
                    <InteractiveStatCard
                      color={stat.color}
                      onClick={() => console.log(`${stat.label} clicked`)}
                      sx={{
                        p: 2,
                        textAlign: "center",
                        boxShadow: "none",
                        border: "1px solid #eef2f7",
                      }}
                    >
                      <Typography variant="caption" sx={{ color: "#8a8a9a", display: "block" }}>
                        {stat.label}
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: "#1a1a2e" }}>
                        {stat.value}
                      </Typography>
                      <Typography variant="caption" sx={{ color: stat.changeColor }}>
                        {stat.change}
                      </Typography>
                    </InteractiveStatCard>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Overview;